import { useLocation } from 'react-router-dom';
import { Moon, Sun, LogOut, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { logoutUser } from '@/store/auth/authSlice';
import { OrgSelector } from './OrgSelector';
import { WorkspaceSelector } from './WorkspaceSelector';
import { useEffect, useState } from 'react';

/** Determine the initial theme from localStorage or OS preference. */
function getInitialTheme(): 'dark' | 'light' {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem('theme');
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Map route paths to human-readable breadcrumb labels. */
const routeLabels: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/users': 'Users',
  '/policies': 'Policies',
  '/jobs': 'Jobs',
  '/services': 'Services',
  '/planning': 'Planning',
  '/settings': 'Settings',
};

export function Header() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const [dark, setDark] = useState(() => getInitialTheme() === 'dark');

  const breadcrumb = routeLabels[location.pathname] ?? 'Cluster';

  // Apply class to <html> and persist to localStorage
  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [dark]);

  // Follow OS preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setDark(e.matches);
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const initials = user
    ? `${user.username?.[0] ?? ''}${user.email?.[0] ?? ''}`.toUpperCase()
    : '??';

  return (
    <header className="flex h-16 items-center justify-between border-b bg-background px-6">
      <h2 className="text-lg font-semibold">{breadcrumb}</h2>

      <div className="flex items-center gap-2">
        {/* Organization selector (header switch-org-session) */}
        <OrgSelector />

        {/* Workspace selector (extension of the org switch; hidden until org scope) */}
        <WorkspaceSelector />

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setDark(!dark)}
          aria-label="Toggle theme"
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.username ?? 'Admin'}</p>
                <p className="text-xs text-muted-foreground">{user?.email ?? ''}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
